import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Music, MapPin, Globe } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import ConsumerLayout from '@/components/ConsumerLayout';
import { useLocation } from '@/contexts/LocationContext';
import api from '@/lib/api';

export default function CreatorDirectory() {
  const { currentCity } = useLocation();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const params = new URLSearchParams();
    if (currentCity?.city) params.set('city', currentCity.city);
    if (filter) params.set('hub_type', filter);
    params.set('limit', '50');
    api.get(`/creators/directory?${params.toString()}`).then(r => setCreators(r.data.creators || [])).catch(() => {}).finally(() => setLoading(false));
  }, [currentCity, filter]);

  const filtered = creators.filter(c => !search || c.stage_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-2">Creators</h1>
        <p className="text-slate-400 mb-8">DJs, promoters, venues, and brands on EVENT★GOERS</p>
        
        <div className="flex gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input placeholder="Search creators..." value={search} onChange={e => setSearch(e.target.value)} className="pl-12 bg-slate-800/50 border-slate-700 text-white h-12" />
          </div>
          <div className="flex gap-2">
            {['', 'dj', 'promoter', 'venue', 'brand'].map(t => (
              <Badge key={t} variant={filter === t ? 'default' : 'outline'} className="cursor-pointer px-3 py-2" onClick={() => setFilter(t)}>{t || 'All'}</Badge>
            ))}
          </div>
        </div>

        {loading ? <div className="text-center py-20 text-slate-400">Loading...</div> : filtered.length === 0 ? (
          <div className="text-center py-20"><Users className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">No creators found</p></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {filtered.map(creator => (
              <Link key={creator.id} to={`/creators/${creator.handle}`}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all text-center group h-full">
                  <CardContent className="p-6">
                    <div className="w-20 h-20 rounded-full mx-auto mb-3 overflow-hidden border-2 border-slate-700 group-hover:border-indigo-500 transition-colors" style={{ backgroundColor: `${creator.accent_color}22` }}>
                      {creator.avatar ? <img src={creator.avatar} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ color: creator.accent_color }}>{creator.stage_name?.charAt(0)}</div>}
                    </div>
                    <h3 className="font-bold text-white group-hover:text-indigo-400 transition-colors">{creator.stage_name}</h3>
                    <p className="text-xs text-slate-500 capitalize mt-1">{creator.hub_type}</p>
                    {creator.genres?.length > 0 && <div className="flex flex-wrap gap-1 justify-center mt-2">{creator.genres.slice(0,2).map(g => <Badge key={g} variant="secondary" className="text-[9px]">{g}</Badge>)}</div>}
                    <p className="text-[10px] text-slate-600 mt-2">{creator.followers_count || 0} followers</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
